import React, { useState, useEffect } from 'react';

const METRICS = [
  { key: 'diluted_cplh', label: 'Diluted CPLH' },
  { key: 'direct_cplh', label: 'Direct CPLH' },
  { key: 'ib_cplh', label: 'IB CPLH' },
  { key: 'ob_cplh', label: 'OB CPLH' },
  { key: 'pid_dept', label: 'PID Dept' },
  { key: 'fluid_total', label: 'Fluid Total' },
  { key: 'fluid_direct', label: 'Fluid Direct' },
  { key: 'fluid_offline', label: 'Fluid Offline' },
  { key: 'eol', label: 'EOL' },
  { key: 'aros_ib', label: 'AROS Inbound' },
  { key: 'aros_ob', label: 'AROS Outbound' },
  { key: 'ib_pr', label: 'IB PR' },
  { key: 'pit', label: 'PIT' },
  { key: 'total_ib_cartons', label: 'Total IB Cartons' },
  { key: 'total_ob_cartons', label: 'Total OB Cartons' },
  { key: 'sorter_cartons', label: 'Sorter Cartons' },
  { key: 'aros_cartons', label: 'AROS Cartons' },
  { key: 'pr_cartons', label: 'PR Cartons' },
  { key: 'fluid_cartons', label: 'Fluid Cartons' },
  { key: 'pit_cartons', label: 'PIT Cartons' },
  { key: 'pid_unloader', label: 'PID Unloader' },
  { key: 'pid_merge', label: 'PID Merge' },
  { key: 'pid_operator', label: 'PID Operator' },
  { key: 'direct_wallbuild', label: 'Direct Wallbuild' },
  { key: 'jam_clear', label: 'Jam Clear' },
  { key: 'fluid_ws', label: 'Fluid WS' },
  { key: 'offline_wallbuild', label: 'Offline WallBuild' },
  { key: 'pr_unload', label: 'PR Unload' },
  { key: 'pallet_build', label: 'Pallet Build' },
  { key: 'eol_avery', label: 'EOL Avery' },
  { key: 'eol_divert', label: 'EOL Divert' },
  { key: 'eol_sort', label: 'EOL Sort' },
  { key: 'eol_waterspider', label: 'EOL Waterspider' },
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wed', 'Thursday', 'Friday', 'Saturday'];

function extractMetric(actual, key) {
  // If pre-computed value exists (from CSV upload), use it directly
  if (key in actual && actual[key] !== undefined && actual[key] !== null) {
    const val = parseFloat(actual[key]);
    if (!isNaN(val)) return val;
  }

  const c = actual.cartons || {};
  const ppr = actual.ppr_hours || {};
  const labor = actual.labor || [];

  const fcHrs = Object.values(ppr.fcSummary || {}).reduce((s, v) => s + v, 0);
  const ibHrs = Object.values(ppr.inbound || {}).reduce((s, v) => s + v, 0);
  const obHrs = Object.values(ppr.transferOut || {}).reduce((s, v) => s + v, 0);
  const ibCartons = c.inbound?.total || 0;
  const obCartons = c.outbound?.total || 0;

  const getLaborHrs = (name) => { const r = labor.find(l => l.process_path === name); return r?.total || 0; };
  const getLaborRate = (name, cartons) => { const hrs = getLaborHrs(name); return hrs > 0 ? cartons / hrs : 0; };
  const sumLaborHrs = (names) => names.reduce((s, n) => s + getLaborHrs(n), 0);
  const deptRate = (names, cartons) => { const hrs = sumLaborHrs(names); return hrs > 0 ? cartons / hrs : 0; };

  const pidCartons = c.pid?.total || 0;
  const arosCartons = c.aros?.total || 0;
  const prCartons = c.pr?.total || 0;
  const fluidDirect = c.fluidDirect?.total || 0;
  const fluidOffline = c.fluidOffline?.total || 0;
  const pitCartons = c.pit?.total || 0;
  const eolCartons = c.eol?.total ?? Math.max(0, pidCartons - fluidDirect);

  const map = {
    direct_cplh: (ibHrs + obHrs) > 0 ? (ibCartons + obCartons) / (ibHrs + obHrs) : 0,
    diluted_cplh: fcHrs > 0 ? (ibCartons + obCartons) / fcHrs : 0,
    ib_cplh: ibHrs > 0 ? ibCartons / ibHrs : 0,
    ob_cplh: obHrs > 0 ? obCartons / obHrs : 0,
    pid_dept: deptRate(['PID Unload', 'PID Divert', 'PID Reinject', 'PID Operator'], pidCartons),
    fluid_total: deptRate(['Sorter Wallbuild', 'Jam Clear', 'Fluid WS', 'Offline Wallbuild'], fluidDirect + fluidOffline),
    fluid_direct: deptRate(['Sorter Wallbuild'], fluidDirect),
    fluid_offline: deptRate(['Offline Wallbuild', 'Fluid WS'], fluidOffline),
    eol: deptRate(['EOL Avery', 'EOL Divert', 'EOL Sort', 'EOL Waterspider'], eolCartons),
    aros_ib: deptRate(['AROS Unload', 'AROS Operator', 'AROS CSX Label'], arosCartons),
    aros_ob: deptRate(['AROS Avery', 'AROS Divert', 'AROS Sort', 'AROS Waterspider'], arosCartons),
    ib_pr: deptRate(['PR Unload', 'Pallet Build', 'Wrap/Lantech', 'PR Waterspider', 'IB Pallet EPJ WS'], prCartons),
    pit: deptRate(['PIT Unloader', 'OB EPJ WaterSpider', 'Pallet Loader', 'OB EPJ Operator'], pitCartons),
    total_ib_cartons: ibCartons, total_ob_cartons: obCartons,
    sorter_cartons: pidCartons - fluidDirect, aros_cartons: arosCartons,
    pr_cartons: prCartons, fluid_cartons: fluidDirect + fluidOffline, pit_cartons: pitCartons,
    pid_unloader: getLaborRate('PID Unload', pidCartons), pid_merge: getLaborRate('PID Divert', pidCartons),
    pid_operator: getLaborRate('PID Operator', pidCartons),
    direct_wallbuild: getLaborRate('Sorter Wallbuild', fluidDirect), jam_clear: getLaborRate('Jam Clear', pidCartons),
    fluid_ws: getLaborRate('Fluid WS', fluidOffline), offline_wallbuild: getLaborRate('Offline Wallbuild', fluidOffline),
    pr_unload: getLaborRate('PR Unload', prCartons), pallet_build: getLaborRate('Pallet Build', prCartons),
    eol_avery: getLaborRate('EOL Avery', eolCartons), eol_divert: getLaborRate('EOL Divert', eolCartons),
    eol_sort: getLaborRate('EOL Sort', eolCartons), eol_waterspider: getLaborRate('EOL Waterspider', eolCartons),
  };
  return map[key] || 0;
}

function getHeatColor(value, avg) {
  if (!avg || !value) return '#f5f5f5';
  const ratio = value / avg;
  if (ratio >= 1.05) return '#c8e6c9';
  if (ratio >= 1.0) return '#e8f5e9';
  if (ratio >= 0.95) return '#fff9c4';
  if (ratio >= 0.90) return '#ffcc80';
  return '#ef9a9a';
}

function getWeekStart(date) {
  const d = new Date(date + 'T12:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d.toISOString().split('T')[0];
}

function formatWeekLabel(weekStart) {
  const d = new Date(weekStart + 'T12:00:00');
  const month = d.getMonth() + 1;
  const day = d.getDate();
  // Get ISO week number
  const start = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
  return { dateLabel: `${month}/${day}`, weekNum: `Week ${weekNum}` };
}

export default function Hapmeat({ api, site }) {
  const [actuals, setActuals] = useState([]);
  const [metric1, setMetric1] = useState('diluted_cplh');
  const [metric2, setMetric2] = useState('direct_cplh');
  const [metric3, setMetric3] = useState('ib_cplh');
  const [metric4, setMetric4] = useState('ob_cplh');
  const [weeks, setWeeks] = useState(6);

  useEffect(() => {
    const to = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
    const from = new Date(Date.now() - weeks * 7 * 86400000).toISOString().split('T')[0];
    // Fetch from FCLM cache for each date/shift in range
    const dates = [];
    let d = new Date(from + 'T12:00:00');
    const end = new Date(to + 'T12:00:00');
    while (d <= end) { dates.push(d.toISOString().split('T')[0]); d.setDate(d.getDate() + 1); }
    Promise.all(dates.flatMap(date => ['day','night'].map(shift =>
      fetch(`${api}/api/fclm/cache?site=${site}&date=${date}&shift_type=${shift}`)
        .then(r => r.json())
        .then(data => data?.labor ? { ...data, date, shift_type: shift, site } : null)
        .catch(() => null)
    ))).then(results => {
      const data = results.filter(Boolean);
      const withCorners = data.map(a => {
        const d = new Date(a.date + 'T12:00:00');
        const day = d.getDay();
        const isNight = a.shift_type === 'night';
        let corner;
        if (day === 3) corner = isNight ? 'WEDN' : 'WEDD';
        else if (day >= 0 && day <= 2) corner = isNight ? 'FHN' : 'FHD';
        else corner = isNight ? 'BHN' : 'BHD';
        return { ...a, corner };
      });
      // Dedupe: prefer items with cartons over those without
      const deduped = [];
      const seen = {};
      for (const item of withCorners) {
        const key = `${item.date}#${item.shift_type}`;
        if (!seen[key]) {
          seen[key] = item;
          deduped.push(item);
        } else if (item.cartons && !seen[key].cartons) {
          const idx = deduped.indexOf(seen[key]);
          deduped[idx] = item;
          seen[key] = item;
        }
      }
      setActuals(deduped);
    })
      .catch(() => setActuals([]));
  }, [api, site, weeks]);

  const metrics = [metric1, metric2, metric3, metric4];
  const metricLabels = metrics.map(m => METRICS.find(x => x.key === m)?.label || m);

  // Group actuals by week (Sunday start) and shift type
  const weeklyData = {};
  actuals.forEach(a => {
    const weekStart = getWeekStart(a.date);
    if (!weeklyData[weekStart]) weeklyData[weekStart] = { nights: {}, days: {} };
    const dayOfWeek = new Date(a.date + 'T12:00:00').getDay();
    if (a.shift_type === 'night') weeklyData[weekStart].nights[dayOfWeek] = a;
    else weeklyData[weekStart].days[dayOfWeek] = a;
  });

  // Sort weeks descending
  const sortedWeeks = Object.keys(weeklyData).sort((a, b) => b.localeCompare(a));

  // Compute overall averages per metric for heat coloring
  const overallAvgs = {};
  metrics.forEach(m => {
    const vals = actuals.map(a => extractMetric(a, m)).filter(v => v > 0);
    overallAvgs[m] = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  });

  // Compute shift type averages for ALL corners
  const shiftAvgs = {};
  ['FHN', 'FHD', 'BHN', 'BHD'].forEach(corner => {
    shiftAvgs[corner] = {};
    const cornerActuals = actuals.filter(a => a.corner === corner);
    metrics.forEach(m => {
      const vals = cornerActuals.map(a => extractMetric(a, m)).filter(v => v > 0);
      shiftAvgs[corner][m] = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
    });
  });

  const fmtVal = (v, m) => {
    if (!v) return '—';
    return m.includes('cartons') ? Math.round(v).toLocaleString() : v.toFixed(1);
  };

  const cellStyle = { padding: '3px 6px', textAlign: 'center', fontSize: '0.72rem', fontWeight: 500, border: '1px solid #ddd' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#f5f5f5' };
  const metricColors = ['#1565c0', '#c62828', '#2e7d32', '#f57c00'];

  const MetricSelector = ({ value, onChange, idx }) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ minWidth: 140, fontSize: '0.8rem', borderColor: metricColors[idx], borderWidth: 2 }}>
      {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
    </select>
  );

  return (
    <div>
      <h2 className="section-title">Hapmeat — {site}</h2>
      <div className="card">
        <div className="form-row" style={{ flexWrap: 'wrap' }}>
          <div className="form-group">
            <label style={{ color: metricColors[0] }}>Metric 1</label>
            <MetricSelector value={metric1} onChange={setMetric1} idx={0} />
          </div>
          <div className="form-group">
            <label style={{ color: metricColors[1] }}>Metric 2</label>
            <MetricSelector value={metric2} onChange={setMetric2} idx={1} />
          </div>
          <div className="form-group">
            <label style={{ color: metricColors[2] }}>Metric 3</label>
            <MetricSelector value={metric3} onChange={setMetric3} idx={2} />
          </div>
          <div className="form-group">
            <label style={{ color: metricColors[3] }}>Metric 4</label>
            <MetricSelector value={metric4} onChange={setMetric4} idx={3} />
          </div>
          <div className="form-group">
            <label>Timeframe</label>
            <select value={weeks} onChange={e => setWeeks(Number(e.target.value))}>
              <option value={4}>Last 4 Weeks</option>
              <option value={6}>Last 6 Weeks</option>
              <option value={8}>Last 8 Weeks</option>
              <option value={12}>Last 12 Weeks</option>
            </select>
          </div>
        </div>
        {/* Shift Avg summary */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: '0.78rem', fontWeight: 600, flexWrap: 'wrap' }}>
          {['FHN', 'FHD', 'BHN', 'BHD'].map(corner => (
            <div key={corner} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px' }}>
              <strong>{corner}:</strong>{' '}
              {metrics.map((m, i) => (
                <span key={m} style={{ color: metricColors[i], marginLeft: 4 }}>{fmtVal(shiftAvgs[corner]?.[m], m)}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.72rem', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, textAlign: 'left' }}></th>
              <th style={headerStyle}></th>
              {DAYS.map(d => <th key={d} style={{ ...headerStyle, minWidth: 70 }}>{d}</th>)}
              <th style={{ ...headerStyle, background: '#e3f2fd' }}>Weekly</th>
            </tr>
          </thead>
          <tbody>
            {sortedWeeks.map(weekStart => {
              const { dateLabel, weekNum } = formatWeekLabel(weekStart);
              const week = weeklyData[weekStart];

              return (
                <React.Fragment key={weekStart}>
                  {/* Nights row */}
                  <tr>
                    <td rowSpan={2} style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, verticalAlign: 'middle', borderRight: '2px solid #000', whiteSpace: 'nowrap' }}>
                      <div>{dateLabel}</div>
                      <div style={{ fontSize: '0.65rem', color: '#666' }}>{weekNum}</div>
                    </td>
                    <td style={{ ...cellStyle, fontWeight: 600, fontSize: '0.65rem' }}>Nights</td>
                    {[0, 1, 2, 3, 4, 5, 6].map(day => {
                      const actual = week.nights[day];
                      return (
                        <td key={day} style={{ ...cellStyle, padding: '2px 4px', border: '2px solid #000' }}>
                          {actual ? metrics.map((m, i) => {
                            const val = extractMetric(actual, m);
                            return <div key={m} style={{ color: metricColors[i], fontSize: '0.68rem' }}>{fmtVal(val, m)}</div>;
                          }) : '—'}
                        </td>
                      );
                    })}
                    <td rowSpan={2} style={{ ...cellStyle, background: '#e3f2fd', verticalAlign: 'middle', border: '2px solid #000', padding: '2px 4px' }}>
                      {metrics.map((m, i) => {
                        const nightVals = Object.values(week.nights).map(a => extractMetric(a, m)).filter(v => v > 0);
                        const dayVals = Object.values(week.days).map(a => extractMetric(a, m)).filter(v => v > 0);
                        const all = [...nightVals, ...dayVals];
                        const avg = all.length > 0 ? all.reduce((s, v) => s + v, 0) / all.length : 0;
                        return <div key={m} style={{ color: metricColors[i], fontWeight: 700, fontSize: '0.7rem' }}>{fmtVal(avg, m)}</div>;
                      })}
                    </td>
                  </tr>
                  {/* Days row */}
                  <tr>
                    <td style={{ ...cellStyle, fontWeight: 600, fontSize: '0.65rem' }}>Days</td>
                    {[0, 1, 2, 3, 4, 5, 6].map(day => {
                      const actual = week.days[day];
                      return (
                        <td key={day} style={{ ...cellStyle, padding: '2px 4px', border: '2px solid #000' }}>
                          {actual ? metrics.map((m, i) => {
                            const val = extractMetric(actual, m);
                            return <div key={m} style={{ color: metricColors[i], fontSize: '0.68rem' }}>{fmtVal(val, m)}</div>;
                          }) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Shift Avg detail panel */}
      <div className="card">
        <h3 className="section-title">Shift Averages</h3>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.8rem', border: '1px solid #000' }}>
          <thead>
            <tr>
              <th style={{ ...cellStyle, background: '#232f3e', color: '#fff' }}>Corner</th>
              {metricLabels.map((label, i) => <th key={i} style={{ ...cellStyle, background: '#232f3e', color: '#fff' }}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {['FHD', 'FHN', 'BHD', 'BHN'].map(corner => (
              <tr key={corner}>
                <td style={{ ...cellStyle, fontWeight: 700 }}>{corner}</td>
                {metrics.map((m, i) => (
                  <td key={m} style={{ ...cellStyle, color: metricColors[i] }}>{fmtVal(shiftAvgs[corner]?.[m], m)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
